/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    InputBase,
    Typography,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import { isArray } from 'lodash';
import { useDebounce } from 'use-lodash-debounce';
// components
import { TableLoading } from 'src/components/loading';
import { NotFound } from 'src/components/empty-content';
import WhatsAppTemplateSelector from '../../../../components/whatsappComponents/WhatsAppTemplateSelector';
import { Iconify } from '../../../../components';
import {
    formatUsageType,
    formatTemplateName,
    stripToPlain,
    getTemplateBody,
} from '../../../../components/whatsappComponents/whatsAppTemplateUtils';
// redux
import { useSelector, dispatch } from '../../../../redux/store';
import { getWhatsappAccountPhones, whatsappMessageSend } from '../../../../redux/slices/whatsappAccount';
import { get10WpTemplateList, getSearchWpTemplateList } from '../../../../redux/slices/whatsAppMessages';
import {
    inputBaseStyle,
    emailBoxStyle,
    focusUnableToContactBoxStyle
} from './styles';
import { getFirstConnectedWhatsappBusinessAccount } from '../../../../utils/whatsappAccountLifecycle';

const WA_GREEN = '#25D366';
const WA_ICON_BG = 'rgba(37,211,102,0.08)';
const WA_SELECTED_BORDER = '#25D366';
const WA_SELECTED_BG = 'rgba(37,211,102,0.02)';

const USAGE_TYPE_COLORS = {
    'FOLLOW_UP': { bg: 'rgba(255,171,0,0.08)', color: '#B76E00' },
    'MARKETING': { bg: 'rgba(133,92,248,0.08)', color: '#855CF8' },
    'COMMUNICATION': { bg: 'rgba(7,141,238,0.08)', color: '#078DEE' },
    'UTILITY': { bg: 'rgba(7,141,238,0.08)', color: '#078DEE' },
    'PROMOTION': { bg: 'rgba(255,171,0,0.08)', color: '#B76E00' },
    'AUTOMATION': { bg: 'rgba(133,92,248,0.08)', color: '#855CF8' },
    'OTHER': { bg: 'rgba(145,158,171,0.08)', color: '#637381' },
};

const WhatsappMailCompose = ({ whatsappMessageSuccess, setWhatsappNotification }) => {
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { followLead } = useSelector((state) => state.lead);
    const { whatsappAccountData } = useSelector((state) => state.whatsappAccount);
    const { wpTemplateListGlobal, loadingForWpTemplate } = useSelector((state) => state.whatsappMessages);
    const { allowedUsageTypes } = useSelector((state) => state.whatsappV2);
    const [loading, setLoading] = useState(false);
    const [focusUnableToContact, setFocusUnableToContact] = useState(false);
    const [subjectUnableContact, setSubjectUnableContact] = useState('');
    const [searchUnableContactSubjectQuery, setSearchUnableContactSubjectQuery] = useState('');
    const debouncedUnableContactSubjectValue = useDebounce(searchUnableContactSubjectQuery, 1000);
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('ALL');
    const connectedWhatsappAccount = getFirstConnectedWhatsappBusinessAccount(whatsappAccountData);

    // Pre-fetch templates on mount so they show immediately when accordion opens
    useEffect(() => {
        const wpAccountId = connectedWhatsappAccount?.id;
        if (practiceId && wpAccountId) {
            setFocusUnableToContact(true);
            dispatch(get10WpTemplateList({ wpAccountId, usageTypeFilter: allowedUsageTypes }));
        }
    }, [practiceId, whatsappAccountData]);

    // Derive unique categories from the template list
    const categories = useMemo(() => {
        if (!isArray(wpTemplateListGlobal) || wpTemplateListGlobal.length === 0) return [];
        const types = [...new Set(wpTemplateListGlobal.map(t => t.usage_type).filter(Boolean))];
        return types;
    }, [wpTemplateListGlobal]);

    // Filter templates by selected category
    const filteredTemplates = useMemo(() => {
        if (!isArray(wpTemplateListGlobal)) return [];
        if (selectedCategory === 'ALL') return wpTemplateListGlobal;
        return wpTemplateListGlobal.filter(t => t.usage_type === selectedCategory);
    }, [wpTemplateListGlobal, selectedCategory]);

    const sendWhatsappMessageEvent = (message) => {
        setLoading(true);
        dispatch(getWhatsappAccountPhones({
            practiceId,
            id: connectedWhatsappAccount?.id,
            handleSuccess: (data) => handleSendRequest(data, message)
        }));
    };

    const handleSendRequest = (data, message) => {
        if (data?.success) {
            const messageData = {
                template_name: message,
                message_type: 'template',
                to: followLead?.lead_contact?.phone || ''
            };
            dispatch(whatsappMessageSend({
                practiceId,
                id: connectedWhatsappAccount?.id,
                isBusinessAccountConnected: true,
                phoneId: data?.phoneId[0]?.id,
                messageData,
                handleSuccess: handleSuccessMessage,
                leadInfo: followLead
            }));
            return;
        }
        setLoading(false);
    };

    const handleSuccessMessage = (data) => {
        setLoading(false);
        if (data?.success) {
            whatsappMessageSuccess();
        }
    };

    const handleSearch = (value) => {
        if (value !== '') {
            dispatch(getSearchWpTemplateList({ wpAccountId: connectedWhatsappAccount?.id, search: value, usageTypeFilter: allowedUsageTypes }));
        }
    };

    useEffect(() => {
        if (searchUnableContactSubjectQuery.length > 0) {
            handleSearch(encodeURIComponent(searchUnableContactSubjectQuery));
        }
    }, [debouncedUnableContactSubjectValue]);

    const handleOnFocus = () => {
        if (practiceId) {
            setFocusUnableToContact(true);
            dispatch(get10WpTemplateList({ wpAccountId: connectedWhatsappAccount?.id, usageTypeFilter: allowedUsageTypes }));
            setWhatsappNotification(false);
        }
    };

    const handleUnableContactSubjectSearch = (data) => {
        const search = data;
        setSubjectUnableContact(search);
        setSearchUnableContactSubjectQuery(search);
    };

    const handlePreview = (row) => {
        setPreviewTemplate(row);
        setPreviewModalOpen(true);
    };

    const handleSendWpMessage = (row) => {
        sendWhatsappMessageEvent(row?.name);
    };

    const handleModalSend = (template) => {
        setPreviewModalOpen(false);
        sendWhatsappMessageEvent(template?.name);
    };

    return (
        <>
            <Box sx={{ ...emailBoxStyle, border: 'none', display: 'flex', p: '16px 0 16px' }}>
                <InputBase
                    placeholder="Search templates"
                    startAdornment={<Iconify icon="eva:search-outline" color="grey.500" width={24} height={24} sx={{ mr: 1 }} />}
                    sx={inputBaseStyle}
                    onChange={(e) => handleUnableContactSubjectSearch(e.target.value)}
                    onFocus={handleOnFocus}
                    value={subjectUnableContact}
                />
            </Box>
            {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 4 }}>
                    <CircularProgress size={20} color="primary" />
                    <Typography variant="body2" color="text.secondary">
                        Sending whatsapp message...
                    </Typography>
                </Box>
            )}
            {!loading && focusUnableToContact && (
                <Box
                    onClick={(e) => e.stopPropagation()}
                    sx={focusUnableToContactBoxStyle}
                >
                    {loadingForWpTemplate &&
                        <TableLoading tableData={loadingForWpTemplate && 'No Found'} pageCount={loadingForWpTemplate && 'No Found'} />
                    }
                    {isArray(wpTemplateListGlobal) && wpTemplateListGlobal.length === 0 &&
                        <NotFound />
                    }
                    {(isArray(wpTemplateListGlobal) && wpTemplateListGlobal.length > 0) && (
                        <>
                            {/* Category filter pills */}
                            {categories.length > 1 && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: '5px',
                                        mb: '10px',
                                        overflowX: 'auto',
                                        '&::-webkit-scrollbar': { height: '0px' },
                                    }}
                                >
                                    <Box
                                        component="span"
                                        onClick={() => setSelectedCategory('ALL')}
                                        sx={{
                                            padding: '4px 10px',
                                            borderRadius: '50px',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap',
                                            transition: 'all 0.12s',
                                            background: selectedCategory === 'ALL' ? '#212B36' : 'rgba(145,158,171,0.08)',
                                            color: selectedCategory === 'ALL' ? '#fff' : '#637381',
                                        }}
                                    >
                                        All
                                    </Box>
                                    {categories.map((cat) => (
                                        <Box
                                            key={cat}
                                            component="span"
                                            onClick={() => setSelectedCategory(cat)}
                                            sx={{
                                                padding: '4px 10px',
                                                borderRadius: '50px',
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                whiteSpace: 'nowrap',
                                                transition: 'all 0.12s',
                                                background: selectedCategory === cat ? '#212B36' : 'rgba(145,158,171,0.08)',
                                                color: selectedCategory === cat ? '#fff' : '#637381',
                                            }}
                                        >
                                            {formatUsageType(cat)}
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            {/* Card-style template list */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '6px',
                                    maxHeight: '240px',
                                    overflowY: 'auto',
                                    '&::-webkit-scrollbar': { width: '3px' },
                                    '&::-webkit-scrollbar-thumb': { background: '#DFE3E8', borderRadius: '2px' },
                                }}
                            >
                                {filteredTemplates.length === 0 && <NotFound />}
                                {filteredTemplates.map((ele) => {
                                    const isSelected = previewTemplate?.name === ele.name;
                                    const bodyPreview = stripToPlain(getTemplateBody(ele));
                                    const catStyle = USAGE_TYPE_COLORS[ele.usage_type] || { bg: 'rgba(145,158,171,0.08)', color: '#637381' };

                                    return (
                                        <Box
                                            key={ele.id || ele.name}
                                            onClick={() => handlePreview(ele)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                padding: '9px 12px',
                                                border: isSelected ? `1px solid ${WA_SELECTED_BORDER}` : '1px solid rgba(145,158,171,0.12)',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                background: isSelected ? WA_SELECTED_BG : '#fff',
                                                transition: 'all 0.12s',
                                                '&:hover': {
                                                    borderColor: 'rgba(145,158,171,0.28)',
                                                    background: '#FAFBFC',
                                                    '& .wa-send-btn': {
                                                        background: 'rgba(37,211,102,0.06)',
                                                        '& .MuiBox-root': { color: WA_GREEN },
                                                    },
                                                },
                                            }}
                                        >
                                            {/* Left icon */}
                                            <Box
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '7px',
                                                    background: WA_ICON_BG,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <Iconify icon="ic:baseline-whatsapp" width={16} height={16} sx={{ color: WA_GREEN }} />
                                            </Box>

                                            {/* Template info */}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        color: '#212B36',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    {formatTemplateName(ele.name)}
                                                </Typography>
                                                {bodyPreview && (
                                                    <Typography
                                                        sx={{
                                                            fontSize: '11px',
                                                            color: '#919EAB',
                                                            mt: '1px',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {bodyPreview}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Category badge */}
                                            {ele.usage_type && (
                                                <Box
                                                    sx={{
                                                        padding: '2px 7px',
                                                        borderRadius: '4px',
                                                        fontSize: '10px',
                                                        fontWeight: 600,
                                                        flexShrink: 0,
                                                        background: catStyle.bg,
                                                        color: catStyle.color,
                                                    }}
                                                >
                                                    {formatUsageType(ele.usage_type)}
                                                </Box>
                                            )}

                                            {/* Send button */}
                                            <Tooltip title="Send" arrow>
                                                <Box
                                                    className="wa-send-btn"
                                                    onClick={(e) => { e.stopPropagation(); handleSendWpMessage(ele); }}
                                                    sx={{
                                                        width: 28,
                                                        height: 28,
                                                        borderRadius: '6px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        flexShrink: 0,
                                                        transition: 'all 0.12s',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    <Box sx={{ color: '#C4CDD5', display: 'flex', alignItems: 'center' }}>
                                                        <Iconify icon="iconamoon:send-fill" width={14} height={14} />
                                                    </Box>
                                                </Box>
                                            </Tooltip>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </>
                    )}
                </Box>
            )}
            <WhatsAppTemplateSelector
                open={previewModalOpen}
                onClose={() => setPreviewModalOpen(false)}
                templates={isArray(wpTemplateListGlobal) ? wpTemplateListGlobal : []}
                selectedTemplate={previewTemplate}
                onSelect={setPreviewTemplate}
                onSend={handleModalSend}
                loading={loading}
                title="WhatsApp Template Preview"
                // Lead detail always has exactly one recipient, so audience
                // rules can be applied to the picker here.
                leadId={followLead?.id || null}
            />
        </>
    );
};

WhatsappMailCompose.propTypes = {
    whatsappMessageSuccess: PropTypes.func,
    setWhatsappNotification: PropTypes.func,
};

export default WhatsappMailCompose;
